import { useEffect, useState } from "react";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, isSubscribed, enablePush, disablePush } from "@/lib/push";

export const NotificationToggle = ({ className = "" }) => {
  const [supported] = useState(pushSupported());
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (supported) isSubscribed().then(setOn);
  }, [supported]);

  if (!supported) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (on) {
        await disablePush();
        setOn(false);
        toast.success("Alerts turned off on this device");
      } else {
        await enablePush();
        setOn(true);
        toast.success("Alerts on — you'll get booking updates on this device");
      }
    } catch (e) {
      toast.error(
        e.message === "denied"
          ? "Notifications are blocked — turn them on in your browser settings"
          : e.message === "unsupported"
            ? "This device doesn't support notifications"
            : "Could not change alerts"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid="notification-toggle"
      title={on ? "Booking alerts are on — tap to turn off" : "Get a notification when bookings change"}
      className={`flex w-full items-center justify-center gap-2 rounded-sm border py-2 text-xs font-medium transition-colors disabled:opacity-60 ${
        on
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          : "border-slate-200 text-slate-600 hover:border-[#E63946] hover:text-[#E63946]"
      } ${className}`}
    >
      {on ? <BellRing size={14} /> : <Bell size={14} />}
      {busy ? "…" : on ? "Alerts on" : "Turn on alerts"}
    </button>
  );
};
